import { Big } from 'big.js';
import { bigToString, EnumExtraFeeProcessingMode, EnumExtraFeeType, ExtraFee, parseBig, QuoteService } from 'quote-domain-core';
import { PricingItemQuantityService } from '../pricing-item/pricing-item-quantity-service';
import { singleton } from 'tsyringe';

@singleton()
export class QuoteServiceExtraFeeService {
    constructor(private readonly pricingItemQuantityService: PricingItemQuantityService) {
    }

    public calculateExtraFeesQtyAndAmount(quoteService: QuoteService): void {
        (quoteService.extraFees ?? []).forEach(extraFee => {
            const extraFeeQty = this.getQuantity(quoteService, extraFee);
            Object.assign(extraFee, { quantity: extraFeeQty });
            const totalAmount = this.getExtraFeeTotal(quoteService, extraFee);
            Object.assign(extraFee, { totalAmount: totalAmount.toString() });
        });
    }

    public getQuantity(quoteService: QuoteService, extraFee: ExtraFee): number {
        switch (this.resolveExtraFeeType(extraFee)) {
            case EnumExtraFeeType.EXTRA_FEE:
                return 1;
            case EnumExtraFeeType.PERCENTAGE_EXTRA_FEE:
                return Number(this.resolveExtraFeePercentage(extraFee));
            default:
                return this.getQuantityFromProcessingMode(quoteService, extraFee);
        }
    }

    public getAmount(quoteService: QuoteService, extraFee: ExtraFee) {
        switch (this.resolveExtraFeeType(extraFee)) {
            case EnumExtraFeeType.PERCENTAGE_EXTRA_FEE:
                return bigToString((parseBig(this.resolveExtraFeePercentage(extraFee)).div(new Big(100))).mul(parseBig(quoteService.appliedPrice)));
            default:
                return extraFee.uniteAmount;
        }
    }

    public getExtraFeeTotal(quoteService: QuoteService, extraFee: ExtraFee): Big {
        switch (this.resolveExtraFeeType(extraFee)) {
            case EnumExtraFeeType.EXTRA_FEE:
                return Big(extraFee.uniteAmount || 0);
            case EnumExtraFeeType.PERCENTAGE_EXTRA_FEE:
                return Big(this.resolveExtraFeePercentage(extraFee) || 0).div(new Big(100)).mul(quoteService.appliedPrice);
            default:
                const quantity = this.getQuantityFromProcessingMode(quoteService, extraFee);
                return quantity ? Big(extraFee.uniteAmount || 0).mul(quantity) : new Big(0);
        }
    }

    public getSumOfQuantitiesOnlyForDevicesAndPack(quoteService: QuoteService): number {
        let totalQuantity = 0;
        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(pricingItem => {
                totalQuantity += this.pricingItemQuantityService.getQuantitiesOnlyForDevicesAndPack(pricingItem);
            });
        });
        return totalQuantity;
    }

    public getSumOfQuantitiesForAllTypeOfArticles(quoteService: QuoteService): number {
        const quantityMap: Map<any, number> = new Map();
        let totalQuantity = 0;
        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(pricingItem => {
                this.pricingItemQuantityService.mergeQuantityByArticleCode(pricingItem, quantityMap);
            });
        });
        for (const qt of quantityMap.values()) {
            totalQuantity += qt;
        }
        return totalQuantity;
    }

    private resolveExtraFeeType(extraFee: ExtraFee): string {
        if (extraFee?.type) {
            return extraFee.type;
        }
        switch (extraFee?.processingMode?.code) {
            case EnumExtraFeeProcessingMode.CA_PERCENTAGE:
                return EnumExtraFeeType.PERCENTAGE_EXTRA_FEE;
            case EnumExtraFeeProcessingMode.FIXE:
                return EnumExtraFeeType.EXTRA_FEE;
            default:
                return EnumExtraFeeType.QUANTITY_EXTRA_FEE;
        }
    }

    private resolveExtraFeePercentage(extraFee: ExtraFee): string {
        return extraFee?.percentage ?? extraFee?.uniteAmount;
    }

    private getQuantityFromProcessingMode(quoteService: QuoteService, extraFee: ExtraFee): number {
        let extraFeeQuantity;
        switch (extraFee?.processingMode?.code) {
            case EnumExtraFeeProcessingMode.NBR_DEVICES:
                extraFeeQuantity = this.getSumOfQuantitiesOnlyForDevicesAndPack(quoteService);
                break;
            case EnumExtraFeeProcessingMode.NBR_TOTAL_ARTICLES_STOCK:
                extraFeeQuantity = this.getSumOfQuantitiesForAllTypeOfArticles(quoteService);
                break;
            case EnumExtraFeeProcessingMode.NBRE_DE_PORTEURS:
                extraFeeQuantity = quoteService.userInputs?.totalNumberOfHolders ? Number(quoteService.userInputs?.totalNumberOfHolders) : 0;
                break;
            default:
                extraFeeQuantity = extraFee.quantity;
        }
        return Number(extraFeeQuantity);
    }
}
